<template>
  <div>
    <div>
      Connector actions allow Recurly Engage to perform a variety of functions against third-party
      services. See the
      <a target="_blank" href="https://docs.recurly.com/recurly-engage/docs/connector-actions"
        >documentation</a
      >
      for details and instructions for specific integrations.
    </div>
    <div v-for="section in sections" :key="section.key">
      <h2 class="text-heading-1 text-beige-90 pt-7 pb-1">{{ section.name }}</h2>
      <div class="mt-2 grid grid-cols-4 gap-4">
        <div
          v-for="item in getProviderList(section.key)"
          :key="item.key"
          class="h-36 basis-1/4"
          :data-rf-provider="item.key"
        >
          <RfButton
            class="connector-item-inner bg-[#f5f5f5]!"
            block
            :disabled="userStore.isDisabledWithMessage"
            @click="showConnectorDialog(item)"
          >
            <img :src="item.src" :width="item.width" />
            <div class="connector-error-msg">
              <v-icon
                v-if="
                  app.providers[item.key] &&
                  app.providers[item.key].active &&
                  app.providers[item.key].status === 'error'
                "
                size="14"
                :color="'error'"
                >md:lens</v-icon
              >
            </div>
          </RfButton>
        </div>
      </div>
    </div>
    <RfFigmaConnector
      ref="figmaConnector"
      :app="app"
      :figmaConnector="appsStore.currAppConnectors?.figma"
      @refresh="appsStore.getAppConnectors(app.id)"
    />
    <RfConnectorDialog
      ref="connectorDialog"
      :provider="dialogValue"
      :form="formProviders"
      :app="app"
      :disabled="userStore.isDisabledWithMessage"
      :flagDisabled="providerFlagDisabled"
      :formDirty="formDirty"
      :stripeConnectUrl="stripeConnectUrl"
      :actionDescriptions="actionDescriptions"
      @updateField="onDialogFieldUpdate"
      @save="connectorUpdateClick"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { cloneDeep, isEqual } from "lodash-es";
import RfButton from "@/components/buttons/RfButton.vue";
import RfFigmaConnector from "@/components/RfSettings/RfFigmaConnector.vue";
import RfConnectorDialog from "@/components/RfSettings/connectors/RfConnectorDialog.vue";
import { useActionsStore } from "@/pinia/actionsStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import { PROVIDER_SCHEMAS, SECTIONS, providerSchema } from "@/utils/connectors/providerSchemas";

const props = defineProps({ app: { type: Object, required: true } });
const emit = defineEmits(["onSettingsUpdateClick"]);

const actionsStore = useActionsStore();
const appsStore = useAppsStore();
const userStore = useUserStore();
const route = useRoute();

const figmaConnector = ref(null);
const connectorDialog = ref(null);
const dialogValue = ref(PROVIDER_SCHEMAS[0]);
const formProviders = ref({});
const sections = SECTIONS;

const connectorActions = computed(() => actionsStore.connectorActions || []);
const formDirty = computed(() => !isEqual(formProviders.value, props.app.providers));
const providerFlagDisabled = computed(() => {
  const flag = dialogValue.value?.flag;
  return flag ? !props.app.flags[flag] : false;
});
const stripeConnectUrl = computed(() => {
  const phaser = import.meta.env.VITE_ENDPOINT;
  return new URL(`${phaser}apps/${props.app.id}/stripe/connect`).href;
});

// Two hardcoded app ids (redfit2/redfit3) use Vindicia and must not see Braintree/Stripe.
const VINDICIA_APPS = [
  "abdeb5f1-3773-4517-981a-2f146c88fabd",
  "6304903d-6a41-4ebd-b4e9-16d520b5c835",
];

/**
 * Returns the provider cards of a settings section, ordered by explicit
 * `position` first, then alphabetically; excludes flag-gated and (for Vindicia
 * apps) conflicting billing providers.
 *
 * @param {string} sectionKey - One of SECTIONS keys (e.g. "billing").
 * @returns {Array<Object>} Provider schema entries to render as cards.
 */
function getProviderList(sectionKey) {
  let providerCopy = PROVIDER_SCHEMAS.filter(({ type, hidden }) => type === sectionKey && !hidden);
  if (VINDICIA_APPS.includes(props.app.id)) {
    providerCopy = providerCopy.filter(item => !["braintree", "stripe"].includes(item.key));
  }
  providerCopy = providerCopy.filter(item => !item.flag || props.app.flags[item.flag]);

  const { withPositions, withoutPositions } = Object.groupBy(providerCopy, el =>
    el.position !== undefined ? "withPositions" : "withoutPositions",
  );

  const sortedWithoutPositions = (withoutPositions ?? []).sort((a, b) =>
    `${a.key}`.localeCompare(`${b.key}`),
  );

  if (withPositions?.length) {
    withPositions.sort((a, b) => a.position - b.position);
    return [...withPositions, ...sortedWithoutPositions];
  }
  return sortedWithoutPositions;
}

/**
 * Merges the dialog's draft providers into the model sent to the API.
 * Called by RfSettings via the template ref before saving the app.
 *
 * @param {Object} model - The app model being saved.
 * @returns {void}
 */
function fillModel(model) {
  model.providers = { ...model.providers, ...formProviders.value };
}

function connectorUpdateClick() {
  emit("onSettingsUpdateClick");
}

/**
 * Opens a connector dialog: clones `app.providers` into the editable draft and
 * seeds schema field defaults (e.g. Recurly region).
 *
 * @param {Object} item - Provider schema entry whose card was clicked.
 * @returns {void}
 */
function showConnectorDialog(item) {
  if (item.key === "figma") {
    figmaConnector.value?.open();
    return;
  }
  dialogValue.value = item;
  formProviders.value = cloneDeep(props.app.providers);
  if (!formProviders.value[item.key]) {
    formProviders.value[item.key] = {};
  }
  for (const field of item.fields || []) {
    if (field.default === undefined) continue;
    const target = field.provider || item.key;
    if (!formProviders.value[target]) formProviders.value[target] = {};
    if (!formProviders.value[target][field.key]) {
      formProviders.value[target][field.key] = field.default;
    }
  }
  connectorDialog.value?.open();
}

/**
 * Maps a connector-actions provider key to its action descriptions for the
 * dialog's actions table.
 *
 * @param {string} provider - Provider key (e.g. "recurly").
 * @returns {string[]} Action descriptions.
 */
function actionDescriptions(provider) {
  return connectorActions.value
    .filter(action => action.provider === provider)
    .map(action => action.args.description);
}

/**
 * Applies a dialog field update to its binding target: the form draft for
 * regular fields, `app.flags` / `app.providers` directly for fields with a
 * `bind` override — same write targets as the legacy v-model bindings.
 *
 * @param {Object} field - FieldDescriptor from the provider schema.
 * @param {*} value - New value for the field.
 * @returns {void}
 */
function onDialogFieldUpdate(field, value) {
  if (field.bind === "flags") {
    // eslint-disable-next-line vue/no-mutating-props -- legacy behavior: writes app.flags directly
    props.app.flags[field.key] = value;
    return;
  }
  if (field.bind === "appProviders") {
    const config = props.app.providers?.[field.provider || dialogValue.value.key];
    if (config) config[field.key] = value;
    return;
  }
  const target = field.provider || dialogValue.value.key;
  if (!formProviders.value[target]) formProviders.value[target] = {};
  formProviders.value[target][field.key] = value;
}

function loadProvider() {
  const connector = route?.params?.additional;
  if (connector) {
    const provider = providerSchema(connector);
    if (provider && !provider.hidden) showConnectorDialog(provider);
  }
}

onMounted(() => {
  loadProvider();
  if (!connectorActions.value.length) actionsStore.getConnectorActions(props.app.id);
});

defineExpose({ fillModel });
</script>
<style lang="scss">
.connector-modal-headline {
  text-transform: capitalize;
}
.connector-item-inner {
  background: #f9f9f9;
  border-radius: 5px;
  height: 100% !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  &:hover {
    background: #eee;
  }
}
.connector-error-msg {
  position: absolute;
  top: 3px;
  right: 8px;
}
</style>
